/*
 * DataTablePagination
 * @module DataTable
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp, ifNotProp } from 'styled-tools';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import * as R from 'ramda';

import { Select } from '~/components/Select';
import { Input } from '~/components/Input';

// $FlowIssue
import { ReactComponent as IconFirst } from '@/icons/ic-prev-first.svg';
// $FlowIssue
import { ReactComponent as IconPrev } from '@/icons/ic-prev.svg';
// $FlowIssue
import { ReactComponent as IconLast } from '@/icons/ic-next-last.svg';
// $FlowIssue
import { ReactComponent as IconNext } from '@/icons/ic-next.svg';

import messages from './messages';

export type DataTablePaginationProps = {
  pages: number,
  page: number,
  pageSize: number,
  pageSizeOptions: number[],
  onPageChange: number => void,
  onPageSizeChange: number => void,
  hidePagination?: boolean,
  device?: $PropertyType<DeviceQuery, 'device'>,
  loading?: boolean,
};

const getSelectOption = (value: number) => ({ value, label: value });
const getSafePage = (value, props) => {
  let page = value;

  if (Number.isNaN(page)) {
    page = R.propOr(1, 'page', props);
  }

  return Math.min(Math.max(page, 1), R.propOr(1, 'pages', props));
};

const DataTablePaginationWrapper = styled.div.attrs(() => ({
  testid: 'datatablepagination',
}))`
  display: flex;
  padding-top: 12px;

  ${ifProp(
    { device: 'desktop' },
    css`
      justify-content: space-between;
    `,
    css`
      flex-direction: column;
    `,
  )}

  span {
    margin-right: 13px;
    color: ${palette('grayscale', 2)};
  }
`;

const DataTablePaginationNavigationWrapper = styled.div`
  display: flex;
  align-items: center;
  ${ifNotProp(
    { device: 'desktop' },
    css`
      justify-content: space-between;
    `,
  )}
`;

const DataTablePaginationNavigation = styled.div`
  display: flex;
  align-items: center;
  margin-right: 37px;
`;

const DataTablePaginationSizeWrapper = styled.div`
  display: flex;
  align-items: center;

  ${ifNotProp(
    { device: 'desktop' },
    css`
      margin: 8px -18px 0;
      padding: 2px 18px 8px;
      justify-content: space-between;
      background-color: #f2f2f2;
    `,
  )}
`;

const DataTablePaginationPageInput = styled(Input).attrs(() => ({
  size: 'small',
  simple: true,
}))`
  padding: 0;
  width: 30px;
  min-width: 30px;
  text-align: center;

  & > input {
    padding: 0 2px;
    text-align: center;
  }
`;

const DataTablePaginationPageSizeSelect = styled(Select).attrs(() => ({
  isSearchable: false,
}))`
  margin-right: 17px;
  width: 64px;
`;

const DataTablePaginationIcon = styled.div`
  ${ifProp(
    { offset: 'right' },
    css`
      margin-right: 20px;
    `,
    css`
      margin-left: 20px;
    `,
  )};
  cursor: ${ifProp('disabled', 'default', 'pointer')};
  pointer-events: ${ifProp('disabled', 'none', 'auto')};
  transition: all 250ms ease-out;
  ${ifProp(
    'disabled',
    css`
      opacity: 0.5;
    `,
  )};
`;

const DataTablePaginationShowAll = styled.div`
  ${ifProp(
    { device: 'desktop' },
    css`
      margin-left: 49px;
    `,
    css`
      justify-content: space-between;
    `,
  )}
  cursor: pointer;
  pointer-events: auto;
`;

/**
 * DataTablePagination React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {number} [props.pages] - pages length
 * @param {number} [props.page = 0] - current page
 * @param {number} [props.pageSize = 10] - items per page
 * @param {boolean} [props.canPrevious] - can go to the page less current
 * @param {boolean} [props.canNext] - can go to the page greater current
 * @param {number[]} [props.pageSizeOptions = []] - available list of items per page
 * @param {Function} [props.onPageChange = () => {}] - on change page callback
 * @param {Function} [props.onPageSizeChange = () => {}] - on change items per page callback
 * @example <caption>Simple usage</caption>
 * <DataTablePagination pages={1} >
 * @returns {React.Node<*>} React-component
 */
export const DataTablePagination = ({
  pages,
  page,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  hidePagination,
  device,
  loading,
}: DataTablePaginationProps) => {
  if (hidePagination || loading) {
    return null;
  }

  const canPrevious = page > 1;
  const canNext = page < pages;

  return (
    <DataTablePaginationWrapper testid="datatablepagination" device={device}>
      <DataTablePaginationNavigationWrapper device={device}>
        <DataTablePaginationNavigation>
          <DataTablePaginationIcon
            disabled={!canPrevious}
            offset="right"
            onClick={() => onPageChange(getSafePage(1, { page, pages }))}
            as={IconFirst}
          />
          <DataTablePaginationIcon
            disabled={!canPrevious}
            offset="right"
            as={IconPrev}
            onClick={() => onPageChange(getSafePage(page - 1, { page, pages }))}
          />
          <DataTablePaginationPageInput
            value={page}
            onChange={value =>
              onPageChange(getSafePage(value, { page, pages }))
            }
            pattern="\d*"
          />
          <DataTablePaginationIcon
            disabled={!canNext}
            onClick={() => onPageChange(getSafePage(page + 1, { page, pages }))}
            as={IconNext}
          />
          <DataTablePaginationIcon
            disabled={!canNext}
            onClick={() => onPageChange(getSafePage(pages, { page, pages }))}
            as={IconLast}
          />
        </DataTablePaginationNavigation>
        <FormattedHTMLMessage
          {...messages.of}
          values={{ pages }}
          tagName="div"
        />
      </DataTablePaginationNavigationWrapper>
      <DataTablePaginationSizeWrapper device={device}>
        <DataTablePaginationPageSizeSelect
          value={getSelectOption(pageSize)}
          options={pageSizeOptions.map(getSelectOption)}
          onChange={({ value }) => onPageSizeChange(value)}
          top
        />
        <FormattedMessage {...messages.perPage} tagName="span" />
        <DataTablePaginationShowAll
          onClick={() => onPageSizeChange(pages * pageSize)}
        >
          <FormattedMessage {...messages.showAll} />
        </DataTablePaginationShowAll>
      </DataTablePaginationSizeWrapper>
    </DataTablePaginationWrapper>
  );
};

DataTablePagination.defaultProps = {
  page: 0,
  pageSize: 10,
  pageSizeOptions: [],
  onPageChange: () => {},
  onPageSizeChange: () => {},
};
